/**
 * Persistence contract for everything the reader saves against a document.
 *
 * The shell reaches storage only through this interface, the same way it
 * reaches the document only through `ReaderController`. Two consequences we
 * actually want:
 *
 *   - the localStorage demo, the eventual GraphQL client, and a test fake are
 *     interchangeable without the shell noticing;
 *   - SPEC Phase 1 gets its second seam — a fake service plus a fake adapter
 *     drives the whole shell with no backend and no renderer.
 *
 * Shaped to match the backend it will eventually talk to, so the swap is a
 * transport change rather than a refactor. From `schema.graphql`:
 *
 *   courseMaterialHighlightManage(courseMaterialId, highlightData: JSON!,
 *                                 highlightId: String!): ClientCourseMaterialHighlight!
 *   courseMaterialHighlightDelete(courseMaterialId, highlightId): Boolean!
 *
 * Three things that contract tells us, and this one honours:
 *
 *   1. **Async from day one.** The demo impl just resolves immediately, so
 *      callers already handle latency and failure before the real client lands.
 *   2. **The service owns the id, and `add*` returns the created record** —
 *      never the whole collection. Note that `Manage` takes a *client-supplied*
 *      `highlightId`, so "owns" means the service mints it, not the server; the
 *      caller still never does.
 *   3. **`Manage` is an upsert**, covering create and update in one operation.
 *      An `updateHighlight` here would map onto the same call — added when
 *      Phase 2 gives a highlight a note body and something actually needs it.
 *
 * Flashcards will join this interface with the identical shape
 * (`courseMaterialFlashcardManage`/`Delete` already exist server-side).
 * Bookmarks have no mutation of their own and go through the generic
 * `courseMaterialMetadata(key, value: JSON!)` KV, which production already
 * does — see the bookmarks section below for what that costs.
 *
 * Vocabulary: types and methods here say **highlight**, matching the GraphQL
 * schema and the renderer verbs. The UI says "note" — that is the client's word
 * for the same thing, and it stops at the presentation layer. See `ReaderShell`.
 */

import type { ReaderLocatorEnvelope } from '../types';

/** A persisted highlight: the renderer-agnostic locator plus presentation. */
export type StoredHighlight = {
  id: string;
  createdAt: string;
  envelope: ReaderLocatorEnvelope;
  color?: string;
  /**
   * The reader's own words. Optional, and its absence is meaningful: a
   * highlight with no body is a perfectly good note — the product counts both
   * in one panel under one word.
   */
  body?: string;
};

/** What the caller supplies; the service fills in `id` and `createdAt`. */
export type StoredHighlightInput = {
  envelope: ReaderLocatorEnvelope;
  color?: string;
  body?: string;
};

/** Fields a saved highlight can change after the fact. */
export type StoredHighlightPatch = {
  body?: string;
  color?: string;
};

/**
 * A saved place in the document.
 *
 * The same envelope a highlight carries, with `kind: 'progress'` instead of
 * `'highlight'` — a bookmark is a position, not a passage, and the schema
 * already draws that distinction. Storing it as an envelope rather than a page
 * number is what lets an EPUB have bookmarks at all.
 */
export type StoredBookmark = {
  id: string;
  createdAt: string;
  envelope: ReaderLocatorEnvelope;
  /**
   * What the reader called it. Optional: the envelope's own display label
   * ("Page 12", a chapter title) is a perfectly good name, and making people
   * type one before they can save a place is how bookmarks go unused.
   */
  label?: string;
};

export type StoredBookmarkInput = {
  envelope: ReaderLocatorEnvelope;
  label?: string;
};

export type ReaderDataService = ReaderFlashcardService &
  ReaderBookmarkService & {
    getHighlights(documentId: string): Promise<StoredHighlight[]>;
    /** Resolves with the created highlight, id assigned by the service. */
    addHighlight(
      documentId: string,
      input: StoredHighlightInput
    ): Promise<StoredHighlight>;
    /**
     * Amend a saved highlight. Maps onto the same `courseMaterialHighlightManage`
     * upsert as `addHighlight` — the id already exists, so the backend updates
     * rather than inserts. Resolves with the amended record.
     */
    updateHighlight(
      documentId: string,
      id: string,
      patch: StoredHighlightPatch
    ): Promise<StoredHighlight>;
    removeHighlight(documentId: string, id: string): Promise<void>;
  };

/* ------------------------------------------------------------------ *
 * Flashcards
 *
 * Field names deliberately match what production already stores in
 * `flashcardData` (see `src/hooks/useFlashcards.ts`), so a card written by
 * either reader round-trips through the other. Three constraints come from
 * that hook and are not ours to invent:
 *
 *   1. **Decks have no backend entity.** They are persisted as a flashcard
 *      with the reserved id `'decks'`, whose `flashcardData` is the array of
 *      decks. That is an implementation detail of the GraphQL adapter — this
 *      contract exposes decks as decks, and the localStorage impl is free to
 *      store them however it likes.
 *   2. **A default deck always exists** (`'default'` / "Default Deck") and is
 *      synthesised client-side rather than stored. Cards with an unknown or
 *      missing `deckId` fall back to it.
 *   3. **Deleting a deck deletes its cards.** Production does this, so we must
 *      too, or the same data read by the other reader would strand orphans.
 *
 * `confidence` is what production stores. The design system's Flashcard shows
 * spaced-repetition ratings (Again / Hard / Good) instead — see
 * `docs/reader-poc/open-questions.md` for why those are not obviously the same
 * thing.
 * ------------------------------------------------------------------ */

export const DEFAULT_DECK_ID = 'default';
export const DEFAULT_DECK_NAME = 'Default Deck';

export type FlashcardConfidence = 'low' | 'medium' | 'high';

export type StoredDeck = {
  id: string;
  name: string;
};

export type StoredFlashcard = {
  id: string;
  createdAt: string;
  deckId: string;
  frontSide: string;
  backSide: string;
  confidence?: FlashcardConfidence;
  /**
   * Where the card came from, when made from a selection. Production stores a
   * PDF-shaped `{pageNumber, content}`; ours is the renderer-agnostic envelope,
   * which is the one field that will need mapping in the GraphQL adapter.
   */
  envelope?: ReaderLocatorEnvelope;
};

export type StoredFlashcardInput = {
  deckId?: string;
  frontSide: string;
  backSide: string;
  envelope?: ReaderLocatorEnvelope;
};

export type StoredFlashcardPatch = {
  deckId?: string;
  frontSide?: string;
  backSide?: string;
  confidence?: FlashcardConfidence;
};

export type ReaderFlashcardService = {
  getDecks(documentId: string): Promise<StoredDeck[]>;
  addDeck(documentId: string, name: string): Promise<StoredDeck>;
  renameDeck(documentId: string, id: string, name: string): Promise<StoredDeck>;
  /** Also removes every card in the deck, matching production. */
  removeDeck(documentId: string, id: string): Promise<void>;

  getFlashcards(documentId: string): Promise<StoredFlashcard[]>;
  addFlashcard(
    documentId: string,
    input: StoredFlashcardInput
  ): Promise<StoredFlashcard>;
  updateFlashcard(
    documentId: string,
    id: string,
    patch: StoredFlashcardPatch
  ): Promise<StoredFlashcard>;
  removeFlashcard(documentId: string, id: string): Promise<void>;
};

/* ------------------------------------------------------------------ *
 * Bookmarks
 *
 * The one collection here with **no** dedicated mutation, and that is a fact
 * about the backend rather than a gap in this file. Production stores them
 * through the generic key/value metadata call — `MaterialViewer` reads
 * `clientCourseMaterialMetadata.bookmarks`, `JSON.parse`s it, and writes the
 * whole array back under `MetadataKey.Bookmarks` on every change. Reading
 * position (`current_page`) rides the same mechanism.
 *
 * Two consequences the GraphQL adapter will have to carry, and neither should
 * leak past it:
 *
 *   1. **Every write is a whole-array write.** `addBookmark` and
 *      `removeBookmark` are read-modify-write against one JSON blob, so two
 *      tabs racing lose one of the two edits. Acceptable for bookmarks; it
 *      would not be for highlights, which is presumably why those got real
 *      mutations.
 *   2. **The stored shape is whatever we put there**, unvalidated by the
 *      schema — so it is worth it being the same envelope everything else uses
 *      rather than the shipped viewer's `{page, name}`. Old data will need
 *      reading through a migration on the way in; see `open-questions.md`.
 * ------------------------------------------------------------------ */

export type ReaderBookmarkService = {
  getBookmarks(documentId: string): Promise<StoredBookmark[]>;
  addBookmark(
    documentId: string,
    input: StoredBookmarkInput
  ): Promise<StoredBookmark>;
  removeBookmark(documentId: string, id: string): Promise<void>;
};
